import { extractPhoneNumberDigits } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type ApiResponse } from "../../api/api";
import { IdentifyProviderEnum } from "./types";
import {
  type OtpRequest,
  type RequestOtpErrorResponse,
  type RequestOtpSuccessResponse,
} from "./useRequestOtp";

export function useRequestAuthOtp(
  options: UseMutationOptions<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  > = {}
): UseMutationResult<
  ApiResponse<RequestOtpSuccessResponse>,
  AxiosError<RequestOtpErrorResponse>,
  OtpRequest
> {
  return useMutation<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  >({
    mutationFn: async ({ isSignup, phone }) => {
      const phoneNumber = extractPhoneNumberDigits(phone);
      const phoneNumberInE164 = `+1${phoneNumber}`;

      const cognitoAuth = getCognitoSingleton();

      // Step 1: Try signUp to force backend synchronization (creates Cognito user if not exists).
      // For signup, this is the primary flow. For login, this triggers the PreSignUp Lambda
      // to import the user from the backend into Cognito.
      try {
        await cognitoAuth.signUp(phoneNumberInE164);
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
          identityProvider: IdentifyProviderEnum.cognito,
          isSignup,
        });
        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: true,
          },
        } satisfies ApiResponse<RequestOtpSuccessResponse>;
      } catch (signUpError) {
        if (isSignup) {
          // For signup, signUp failure is a real error
          logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
            error: signUpError,
            metadata: {
              error: signUpError,
              identityProvider: IdentifyProviderEnum.cognito,
              phoneNumber,
            },
          });
          throw signUpError;
        }

        // For login, signUp failure is expected (user already exists), proceed to signIn
        logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
          error: signUpError,
          metadata: {
            identityProvider: IdentifyProviderEnum.cognito,
            phoneNumber,
            fallbackToSignIn: true,
          },
        });
      }

      // Step 2: Fall back to signIn (requestPhoneOtp) — login only
      try {
        await cognitoAuth.requestPhoneOtp(phoneNumberInE164);
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
          identityProvider: IdentifyProviderEnum.cognito,
          isSignup: false,
        });
        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: false,
          },
        } satisfies ApiResponse<RequestOtpSuccessResponse>;
      } catch (error) {
        logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
          error,
          metadata: {
            identityProvider: IdentifyProviderEnum.cognito,
            phoneNumber,
          },
        });
        throw error;
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE,
    },
    ...options,
  });
}
